/**
 * The 2d seat map. Uses an SVG for the stadium view, then when the sections are navigated into,
 * generates an HTML table of all the seats. For the stadium view and seat view, shows availability
 * by highlighting in the club's primary colour.
 */
export default class TwoDSeatMap
{

    #seatsByRow = {};
    #isPanning = false;
    #seatMapStore;
    #container;
    #seatTableSelector = '.o-2dSeatMap__matrix';
    #seatsTablePZ = null;

    constructor(container, store)
    {
        this.#container = container;
        this.#seatMapStore = store;

        this.#seatMapStore.showLoading();
        const svgElement = this.#container.querySelector('.o-2dStadiumMap > svg');
        this.#assignAvailableSectionsOnSvg(svgElement, this.#seatMapStore.sections());
        this.#seatMapStore.hideLoading();

        this.#initZoom(svgElement);

        window.addEventListener('update-selected-and-available', async (e) => {
            if (e.detail.section !== 'blockmap') {
                if (e.detail.allSeatsInSection) {
                    this.#updateSeatsByRow(e.detail.allSeatsInSection, e.detail.selectedMmcIdentifiers, e.detail.availableMmcIdentifiers);
                }
                if (this.#seatsTablePZ === null || this.#seatMapStore.changeMapR === true) {
                    const seatsTable = this.#container.querySelector(this.#seatTableSelector);
                    this.#seatsTablePZ = await this.#initZoom(seatsTable, true);
                    this.#seatMapStore.changeMapR = false;
                    await this.#zoomToFit(this.#seatsTablePZ, seatsTable);
                }
            }
            this.#assignAvailableSectionsOnSvg(svgElement, e.detail.sections);
            this.#seatMapStore.hideLoading();
        });

        window.addEventListener('back-to-blockmap', () => {
            this.#showMap('blockmap');
        });

        svgElement.querySelectorAll('[data-section]').forEach(sectionElem => {
            sectionElem.addEventListener('click', async () => {
                if (sectionElem.classList.contains('available')) {
                    const sectionName = sectionElem.getAttribute('data-section');
                    const section = this.#extractSectionByName(sectionName, this.#seatMapStore.sections());

                    if (section.has_seat_map) {
                        this.#showMap(section);
                    } else {
                        const protectClick = (id) => window.livewire.emit('openSeatSelectQuantityModal', id);
                        authProtectedClick(null, {click: protectClick}, 'click', [section.id])
                    }
                }
            });
        });
    }

    #extractSectionByName(name, sections)
    {
        return sections.find(section => name === section.identifier);
    }

    #assignAvailableSectionsOnSvg(svgElement, availableSections)
    {
        svgElement.querySelectorAll('[data-section]').forEach(sectionElem => {
            const sectionName = sectionElem.getAttribute('data-section');
            const section = this.#extractSectionByName(sectionName, availableSections);
            const available = section && section.available;

            if (available) {
                sectionElem.classList.remove('unavailable');
                sectionElem.classList.add('available');
            } else {
                sectionElem.classList.remove('available');
                sectionElem.classList.add('unavailable');
            }

            if (section && !section.has_seat_map) {
                sectionElem.setAttribute('data-standing', 'true');
            }
        });
    }

    #updateMiniMap = (sectionName) =>
    {
        sectionName = sectionName.replace('S_', '');
        this.#container.querySelectorAll('.o-2dSeatMap__miniMap [data-section]').forEach(sectionElem => {
            const section = sectionElem.getAttribute('data-section');
            if (section === sectionName) {
                sectionElem.classList.add('current');
            } else {
                sectionElem.classList.remove('current');
            }
        })
    }

    #showMap = async (section) =>
    {
        this.#seatMapStore.showLoading();

        if (section === 'blockmap') {
            this.#seatMapStore.selectedSection = 'blockmap';
            await this.#seatMapStore.resetSection();
            this.#seatMapStore.hideBackButton();
            this.#seatMapStore.selectedSectionDisplayName = null;
            this.#seatMapStore.hideLoading();
        } else {
            this.#seatMapStore.selectedSection = section.mmc_identifier;
            this.#seatMapStore.showBackButton();
            this.#seatMapStore.selectedSectionDisplayName = `${section.section_type} ${section.identifier}`;
            this.#seatMapStore.changeMapR = true;
            await this.#seatMapStore.changeMap(section.id, section.mmc_identifier);
            this.#updateMiniMap(this.#seatMapStore.selectedSection);
        }
    }

    #initZoom(zoomArea, forSeats = false) {
        return new Promise((resolve) => {
            const pz = panzoom(zoomArea, {
                minZoom: 0.5,
                boundsPadding: 1,
                // disable double-click zoom
                zoomDoubleClickSpeed: 1,
                onTouch: function(e) {
                    // allow changing section / selecting seats on touch devices
                    return false; // tells the library to not preventDefault.
                }
            });

            // prevent seat choose when zooming
            pz.on('panstart', () => this.#isPanning = true);
            zoomArea.addEventListener('mousedown', () => this.#isPanning = false);

            if (forSeats) {
                window.addEventListener('resize', () => {
                    if (this.#seatMapStore.selectedSection !== 'blockmap') {
                        clearTimeout(resizeTimeout);
                        this.#seatMapStore.showLoading();
                        const resizeTimeout = setTimeout(async () => {
                            const seatsTableRefreshed = this.#container.querySelector(this.#seatTableSelector);
                            await this.#zoomToFit(pz, seatsTableRefreshed);
                            this.#seatMapStore.hideLoading();
                        }, 300);
                    }
                })
            }
            resolve(pz);
        });
    }

    #zoomToFit(pz, seatsTable)
    {
        return new Promise((resolve) => {
            if (seatsTable.querySelectorAll('tbody > tr').length > 0) {
                const seatsTableRect = seatsTable.getBoundingClientRect();
                const parentRect = seatsTable.closest('.o-seatMap__map').getBoundingClientRect();
                const scaleX = parentRect.width / seatsTableRect.width;
                const scaleY = parentRect.height / seatsTableRect.height;
                const initialZoom = Math.min(scaleX, scaleY);
                pz.zoomTo(0, 0, initialZoom);
                pz.moveTo((parentRect.width - (seatsTableRect.width * initialZoom)) / 2, (parentRect.height - (seatsTableRect.height * initialZoom)) / 2);
            }
            resolve();
        });
    }

    getSeatByCoords = (y, x) =>
    {
        if (undefined === (this.#seatsByRow[y] && this.#seatsByRow[y][x])) {
            return null;
        }
        return this.#seatsByRow[y][x];
    }

    isLinked = (direction, y, x) =>
    {
        const seat = this.getSeatByCoords(y, x);

        if (!seat?.link_id) {
            return false;
        }

        if (seat.available || seat.selected) {
            let linked = null;

            if (direction === 'right') {
                linked = this.getSeatByCoords(y, x + 1);
            } else if (direction === 'left') {
                linked = this.getSeatByCoords(y, x - 1);
            }

            return seat.link_id === linked?.seat_id;
        }

        return false;
    }

    getRowName = (coordY) =>
    {
        if (!(coordY in this.#seatsByRow)) {
            return '';
        }

        const seatsInRow = Object.values(this.#seatsByRow[coordY]);

        if (seatsInRow.length === 0) {
            return '';
        }

        return Object.values(this.#seatsByRow[coordY])[0].row;
    }

    getColumnName = (coordX) =>
    {
        const coordXColumnNameMap = Object.keys(this.#seatsByRow).reduce((carry, coordY) => {
                Object.values(this.#seatsByRow[coordY]).forEach((seat) => {
                    if (seat.x in carry) {
                        return;
                    }
                    carry[seat.x] = seat.column;
                });

                return carry;
            },
            {}
        );

        if (coordX in coordXColumnNameMap) {
            return coordXColumnNameMap[coordX];
        }

        return '';
    }

    #calculateCoords(sortedCoords)
    {
        if (!sortedCoords || sortedCoords.length === 0) {
            // seatsByRow are not loaded yet
            return [];
        }

        // The below code is in case there are rows or columnsg in between without any seats
        const firstCoord = sortedCoords[0];
        const lastCoord = sortedCoords[sortedCoords.length - 1]
        const coords = [];

        for (let i = firstCoord; i <= lastCoord; i++) {
            coords.push(i);
        }

        return coords;
    }

    getYCoords = () =>
    {
        const sortedYCoords = Object.keys(this.#seatsByRow).map((y) => parseInt(y, 10)).sort((a, b) => (a - b));
        return this.#calculateCoords(sortedYCoords);
    }

    getXCoords = () =>
    {
        const xCords = Object.keys(this.#seatsByRow).map(coordY => Object.keys(this.#seatsByRow[coordY])).flat();
        const sortedXCoords = [...new Set(xCords)].map((x) => parseInt(x, 10)).sort((a, b) => (a - b));
        return this.#calculateCoords(sortedXCoords);
    }

    #updateSeatsByRow(sectionSeats, selected, available)
    {
        const seats = {};

        sectionSeats.forEach(s => {
            const seat = {
                 ...s,
                 selected: selected.includes(s.mmc_identifier),
                 available: available.includes(s.mmc_identifier),
                 type: this.#getSeatType(s),
             }

            if (seat.y in seats) {
                seats[seat.y] = {
                    ...seats[seat.y],
                    [seat.x]: seat,
                }
            } else {
                seats[seat.y] = {
                    [seat.x]: seat,
                };
            }
        });

        this.#seatsByRow = seats;
    }

    #getSeatType(seat)
    {
        if (typeof seat !== 'object') {
            throw new TypeError();
        }
        let type = 'normal';
        ['is_wheelchair', 'is_accessible', 'is_pa', 'is_audio_commentary', 'restricted_view'].forEach((field) => {
            if (seat[field] !== undefined && seat[field] === true) {
                type = field.replace('is_', '');
            }
        });
        return type;
    }

    clickSeat = (seat) =>
    {
        if (typeof seat !== 'object') {
            throw new TypeError();
        }
        if (this.#isPanning) {
            // Don't allow accidental seat click when dragging the map
            return;
        }

        const protectClick = (seat) => {
            if (seat.available || seat.selected) {
                this.#seatMapStore.showLoading();
                if (seat.available) {
                    window.Livewire.emitTo('basket', 'addToBasket', seat.basketable_type, seat.basketable_id);
                }
                else {
                    const basketItem = this.#seatMapStore.selectedSeats().find((s) => s.mmc_identifier === seat.mmc_identifier);
                    window.Livewire.emitTo('basket', 'removeFromBasket', basketItem.item_id, basketItem.basketable_type);
                }
            }
        }
        authProtectedClick(null, {click: protectClick}, 'click', [seat]);
    }

}
